/**
 * MentorBoard — "Training Mentor". A unit lead's face of the mentor agent: the
 * trainee roster crossed with what each trainee is working toward, plus a
 * composer to set a new target. Board-first — the agent
 * lives here, not in a chat transcript (same pattern as Team Access / the
 * roster planner / the Care Planner schedule); the optional chat side-channel
 * is wired by the connected wrapper.
 *
 * Reads neutral and interdisciplinary on purpose (a nurse lead, a therapy lead,
 * a physician lead all supervise trainees) — no "nurse tool" framing.
 * Presentational only: it renders roster/targets/options and emits
 * intent via callbacks (set a target, drop a target). There are exactly those
 * two, because a target has exactly one verb besides being set: `:drop`.
 * Whether it was REACHED is the fold's `state`, so this file renders that and
 * offers nothing that would write it.
 *
 * Order = the lead's task: the COMPOSER (the thing they came to do — set a
 * target) sits above the ROSTER (the progress they came to check).
 */

import { useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ChevronDown,
  ChevronRight,
  GraduationCap,
  Target,
  Users,
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/clinical'
import { PersonAvatar } from '@/components/access'
import { TargetRow } from '@/components/training'
import type { Skill } from '@/api/endpoints/skills'
import type { SkillTarget } from '@/hooks/useSkills'
import { catalogueSkillTitle } from '@/lib/skillTitle'
import type { ProtocolSummary } from '@/api/endpoints/protocols'
import type {
  TargetDraft,
  TargetState,
  TraineeProgress,
} from './types'

export interface MentorUnitOption {
  id: string
  label: string
}

export interface MentorBoardProps {
  unit: { label: string }
  trainees: TraineeProgress[]
  /** Published components — the lookup that resolves the module ids a target
   *  names to titles in the roster detail. */
  components: ProtocolSummary[]
  /** The tenant skill catalogue: what the composer sets a target ON. A target
   *  names exactly one skill, and the catalogue is where skills come from. */
  skills: Skill[]
  /**
   * The catalogue read's own state. An empty `skills` means three different
   * things — still arriving, failed, or genuinely empty — and only the last one
   * may be told to the lead as "there is nothing to set a target on".
   */
  skillsLoading?: boolean
  skillsError?: boolean
  onRetrySkills?: () => void
  /** Today in the reader's calendar (`todayCivilDate`) — the day an open
   *  target's `due_date` is late against. */
  today: string
  /**
   * Progress enrichment (each trainee's targets) still loading while the roster is
   * already shown — a subtle header hint, never a blocking spinner over the
   * roster (frontend/CLAUDE.md: no spinner over stale/partial data).
   */
  progressLoading?: boolean
  /** Progress enrichment failed — an inline retry banner; the roster still
   *  renders from props (degrade, don't white-screen). */
  progressError?: boolean
  onRetryProgress?: () => void
  /** Set a target. Absent → the composer is hidden (read-only surface). */
  onCreateTarget?: (draft: TargetDraft) => void
  creating?: boolean
  /**
   * Drop a target — the ONLY verb on one. There is deliberately no "mark
   * achieved" beside it: `state` is derived by the fold from the records, so a
   * control that wrote it would be the board disagreeing with the server.
   */
  onDropTarget?: (userId: string, target: SkillTarget['target']) => void
  droppingTargetIds?: string[]
  /** Unit switcher — only when the lead governs several units. */
  unitOptions?: MentorUnitOption[]
  activeUnitId?: string
  onSwitchUnit?: (id: string) => void
  switchingUnit?: boolean
  /**
   * The quiz catalogue, injected as a slot rather than driven by props. It is
   * a CONNECTED section (it lists every state, where `components` above is
   * the published-only set a target's modules resolve against), and this
   * component is otherwise pure — the DEV /preview harness renders it over
   * fixtures with no backend, so a query inside it would break that. Callers
   * with a live API pass <QuizCatalogueSection />; the preview passes nothing.
   */
  quizCatalogue?: ReactNode
  className?: string
}

/**
 * How many of a trainee's targets the fold reports in one state.
 *
 * Counted off `state`, which the FOLD reported — nothing here decides that a
 * target was reached, the records behind the skill did. Counted at the point of
 * display rather than carried on TraineeProgress, because a count stored beside
 * the list it counts is a second writer for one fact.
 */
function countTargets(targets: SkillTarget[], state: TargetState): number {
  return targets.filter((t) => t.target.state === state).length
}

export function MentorBoard({
  unit,
  trainees,
  components,
  skills,
  skillsLoading,
  skillsError,
  onRetrySkills,
  today,
  progressLoading,
  progressError,
  onRetryProgress,
  onCreateTarget,
  creating,
  onDropTarget,
  droppingTargetIds,
  unitOptions,
  activeUnitId,
  onSwitchUnit,
  switchingUnit,
  quizCatalogue,
  className,
}: MentorBoardProps) {
  const { t } = useTranslation()
  const switchable = !!onSwitchUnit && (unitOptions?.length ?? 0) > 1

  const componentTitleById = useMemo(() => {
    const map = new Map<string, string>()
    for (const c of components) map.set(c.id, c.title)
    return map
  }, [components])

  const openTargets = trainees.reduce((n, tr) => n + countTargets(tr.targets, 'open'), 0)
  const achievedTargets = trainees.reduce((n, tr) => n + countTargets(tr.targets, 'achieved'), 0)

  return (
    <div className={cn('mx-auto w-full max-w-4xl space-y-6 p-4', className)}>
      <header className="rounded-[18px] border border-neutral-200 bg-white p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[13px] font-semibold uppercase tracking-wide text-neutral-500">
              {t('mentor.board.title')}
            </p>
            <h1 className="mt-1 truncate text-[21px] font-semibold tracking-[-0.01em] text-neutral-900">
              {unit.label}
            </h1>
          </div>
          {switchable && (
            <div className="shrink-0">
              <label htmlFor="mentor-unit-switch" className="sr-only">
                {t('mentor.board.switchUnit')}
              </label>
              <select
                id="mentor-unit-switch"
                value={activeUnitId ?? ''}
                disabled={switchingUnit}
                onChange={(e) => {
                  if (e.target.value && e.target.value !== activeUnitId) {
                    onSwitchUnit?.(e.target.value)
                  }
                }}
                className="min-h-11 max-w-48 rounded-md border border-input bg-background px-3 py-2 text-[14px] ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
              >
                {(unitOptions ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <p className="mt-2 text-[13px] text-neutral-500">
          {t('mentor.board.summary', {
            trainees: trainees.length,
            openTargets,
            achievedTargets,
          })}
          {progressLoading && (
            <span className="ml-2 text-neutral-400">{t('common.loading', 'Loading…')}</span>
          )}
        </p>
        {progressError && (
          <p className="mt-2 text-[13px] text-neutral-700" role="alert">
            {t('mentor.board.loadError')}{' '}
            {onRetryProgress && (
              <button
                type="button"
                onClick={onRetryProgress}
                className="font-semibold text-primary underline-offset-2 hover:underline"
              >
                {t('actions.retry', 'Retry')}
              </button>
            )}
          </p>
        )}
      </header>

      {onCreateTarget && (
        <TargetComposer
          trainees={trainees}
          skills={skills}
          skillsLoading={skillsLoading}
          skillsError={skillsError}
          onRetrySkills={onRetrySkills}
          today={today}
          onCreateTarget={onCreateTarget}
          creating={creating}
        />
      )}

      <section className="space-y-3">
        <h2 className="flex items-center gap-2 text-[15px] font-semibold text-neutral-900">
          <Users className="h-4 w-4 text-neutral-400" aria-hidden="true" />
          {t('mentor.board.roster.title')}
        </h2>
        {trainees.length === 0 ? (
          <EmptyState
            icon={<Users aria-hidden="true" />}
            title={t('mentor.board.roster.empty.title')}
            description={t('mentor.board.roster.empty.description')}
          />
        ) : (
          <ul className="space-y-2">
            {trainees.map((tr) => (
              <TraineeCard
                key={tr.user.id}
                trainee={tr}
                componentTitleById={componentTitleById}
                today={today}
                onDropTarget={onDropTarget}
                droppingTargetIds={droppingTargetIds}
              />
            ))}
          </ul>
        )}
      </section>

      {quizCatalogue}

    </div>
  )
}

// -----------------------------------------------------------------------------
// Roster
// -----------------------------------------------------------------------------

function TraineeCard({
  trainee,
  componentTitleById,
  today,
  onDropTarget,
  droppingTargetIds,
}: {
  trainee: TraineeProgress
  componentTitleById: Map<string, string>
  today: string
  onDropTarget?: (userId: string, target: SkillTarget['target']) => void
  droppingTargetIds?: string[]
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const hasDetail = trainee.targets.length > 0

  return (
    <li className="rounded-[18px] border border-neutral-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? t('mentor.board.hideDetail') : t('mentor.board.showDetail')}
        className="flex w-full items-center gap-3 rounded-[18px] px-4 py-3 text-left transition active:scale-[0.99] hover:bg-neutral-50"
      >
        <PersonAvatar user={trainee.user} size="lg" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-semibold text-neutral-900">{trainee.user.name}</p>
          <p className="mt-0.5 text-[13px] text-neutral-500">
            {t('mentor.board.targetCounts', {
              open: countTargets(trainee.targets, 'open'),
              achieved: countTargets(trainee.targets, 'achieved'),
            })}
          </p>
        </div>
        {hasDetail ? (
          open ? (
            <ChevronDown className="h-5 w-5 shrink-0 text-neutral-400" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-5 w-5 shrink-0 text-neutral-400" aria-hidden="true" />
          )
        ) : (
          <span className="h-5 w-5 shrink-0" aria-hidden="true" />
        )}
      </button>

      {open && (
        <div className="space-y-4 border-t border-neutral-100 px-4 py-3">
          {/* Targets */}
          <div className="space-y-2">
            <h3 className="flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wide text-neutral-500">
              <Target className="h-3.5 w-3.5 text-neutral-400" aria-hidden="true" />
              {t('mentor.board.targetsHeading')}
            </h3>
            {trainee.targets.length === 0 ? (
              <p className="text-[13px] text-neutral-400">{t('mentor.board.noTargets')}</p>
            ) : (
              <ul className="space-y-2">
                {trainee.targets.map(({ target, skillTitle }) => (
                  <TargetRow
                    key={target.id}
                    target={target}
                    skillTitle={skillTitle}
                    today={today}
                    // An archived module is gone from the published catalogue
                    // but the target still names it — show the id rather than
                    // dropping the line.
                    modules={target.module_ids.map((id) => componentTitleById.get(id) ?? id)}
                    // Only an OPEN target can be dropped: `:drop` on one already
                    // dropped or already reached has nothing to act on, and
                    // offering it would be a control that answers 409.
                    action={
                      onDropTarget && target.state === 'open' ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => onDropTarget(trainee.user.id, target)}
                          disabled={droppingTargetIds?.includes(target.id)}
                          className="min-h-9 text-[12px] font-semibold text-neutral-500 hover:text-destructive"
                        >
                          {droppingTargetIds?.includes(target.id)
                            ? t('mentor.targets.dropping')
                            : t('mentor.targets.drop')}
                        </Button>
                      ) : undefined
                    }
                  />
                ))}
              </ul>
            )}
          </div>

        </div>
      )}
    </li>
  )
}

// -----------------------------------------------------------------------------
// Target composer
// -----------------------------------------------------------------------------

/**
 * Set a target: one trainee, one skill, one day.
 *
 * Three fields and no fourth. A target has no title and no description — the
 * skill IS the subject, and inventing a sentence beside it would be a second
 * home for the same fact. The skill comes from the tenant catalogue rather than
 * from free text, because the path the write goes to is addressed by the
 * skill's id.
 *
 * `due_date` is required by the contract, so the picker starts empty and Save
 * stays disabled until the lead names a day — there is no default date to
 * substitute, and a target dated "in a month because nobody said" is a
 * commitment nobody made.
 */
function TargetComposer({
  trainees,
  skills,
  skillsLoading,
  skillsError,
  onRetrySkills,
  today,
  onCreateTarget,
  creating,
}: {
  trainees: TraineeProgress[]
  skills: Skill[]
  skillsLoading?: boolean
  skillsError?: boolean
  onRetrySkills?: () => void
  today: string
  onCreateTarget: (draft: TargetDraft) => void
  creating?: boolean
}) {
  const { t, i18n } = useTranslation()
  const [subjectUserId, setSubjectUserId] = useState('')
  const [skillKey, setSkillKey] = useState('')
  const [dueDate, setDueDate] = useState('')

  const options = useMemo(
    () =>
      skills
        .map((skill) => ({ id: skill.id, title: catalogueSkillTitle(skill, i18n.language) }))
        .sort((a, b) => a.title.localeCompare(b.title, i18n.language)),
    [skills, i18n.language],
  )

  // A day already gone is a typo, never an intent: the target would be born
  // overdue. The picker floors at today and the composer refuses it anyway.
  const ready = !!subjectUserId && !!skillKey && dueDate >= today && !creating

  const submit = () => {
    if (!ready) return
    onCreateTarget({ subjectUserId, skillKey, dueDate })
    setSubjectUserId('')
    setSkillKey('')
    setDueDate('')
  }

  const selectClass =
    'min-h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'

  return (
    <section className="space-y-3">
      <h2 className="flex items-center gap-2 text-[15px] font-semibold text-neutral-900">
        <GraduationCap className="h-4 w-4 text-neutral-400" aria-hidden="true" />
        {t('mentor.board.newTarget')}
      </h2>
      <div className="space-y-3 rounded-[18px] border border-neutral-200 bg-white p-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <label htmlFor="target-trainee" className="block text-[13px] font-semibold text-neutral-500">
              {t('mentor.targets.trainee')}
            </label>
            <select
              id="target-trainee"
              value={subjectUserId}
              onChange={(e) => setSubjectUserId(e.target.value)}
              className={selectClass}
            >
              <option value="">{t('mentor.targets.traineePlaceholder')}</option>
              {trainees.map((tr) => (
                <option key={tr.user.id} value={tr.user.id}>
                  {tr.user.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label htmlFor="target-due" className="block text-[13px] font-semibold text-neutral-500">
              {t('mentor.targets.dueDate')}
            </label>
            <input
              id="target-due"
              type="date"
              value={dueDate}
              min={today}
              onChange={(e) => setDueDate(e.target.value)}
              className={selectClass}
            />
          </div>
        </div>

        <div className="space-y-1">
          <label htmlFor="target-skill" className="block text-[13px] font-semibold text-neutral-500">
            {t('mentor.targets.skill')}
          </label>
          {/* An empty `options` means three different things and only one of
              them may be reported as "no skills": the read has to have LANDED
              before "the tenant has published none" is an answer the lead can
              act on. In flight it is unknown, and failed it is an error with a
              retry — never a dropdown that quietly offers nothing. */}
          {skillsLoading ? (
            <Skeleton className="h-11 w-full rounded-md" />
          ) : skillsError ? (
            <p className="text-[13px] text-neutral-700" role="alert">
              {t('mentor.targets.skillsError')}{' '}
              {onRetrySkills && (
                <button
                  type="button"
                  onClick={onRetrySkills}
                  className="font-semibold text-primary underline-offset-2 hover:underline"
                >
                  {t('actions.retry', 'Retry')}
                </button>
              )}
            </p>
          ) : options.length === 0 ? (
            <p className="text-[13px] text-neutral-400">{t('mentor.targets.skillsEmpty')}</p>
          ) : (
            <select
              id="target-skill"
              value={skillKey}
              onChange={(e) => setSkillKey(e.target.value)}
              className={selectClass}
            >
              <option value="">{t('mentor.targets.skillPlaceholder')}</option>
              {options.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.title}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="pt-1">
          <Button type="button" size="pill" disabled={!ready} onClick={submit} className="min-h-11">
            {creating ? t('mentor.targets.saving') : t('mentor.targets.save')}
          </Button>
        </div>
      </div>
    </section>
  )
}
